<template>
  <div>
    <div class="reporting-call-logs-header">
      <div class="reporting-call-logs-header__title">
        Call Details
      </div>

      <div class="reporting-call-logs-header__right reporting-call-logs-header__right--overflow">
        <button
          v-if="adminViewingAsAdmin"
          class="btn btn-sm m-r-5"
          :disabled="!selectedCalls.length"
          @click="clearRowSelection"
        >
          <i class="fa fa-minus clear-btn__icon--center" />
          {{ $t('CLEAR_CALLS_SELECTION') }}
        </button>

        <dropdown
          v-if="adminViewingAsAdmin"
          tag="span"
          :menu-right="true"
          class="bulk-actions-dropdown m-r-10"
        >
          <btn
            type="primary"
            size="sm"
          >
            <span class="text-uppercase">
              Edit calls <span v-if="selectedCalls.length">({{ selectedCalls.length }})</span>
            </span>

            <span class="caret m-l-5" />
          </btn>

          <template slot="dropdown">
            <li v-tooltip.left="generalEditTooltip">
              <a
                v-if="adminViewingAsAdmin"
                role="button"
                class="btn bulk-actions-dropdown__btn"
                :disabled="!selectedCalls.length"
                @click="showBulkForm('tagAnnotate')"
              >
                <i class="fa fa-pencil fa-fw" />
                {{ $t('ANNOTATE') }}
              </a>
            </li>

            <li v-tooltip.left="generalEditTooltip">
              <a
                v-if="adminViewingAsAdmin"
                role="button"
                class="btn bulk-actions-dropdown__btn"
                :disabled="!selectedCalls.length"
                @click="showBulkForm('blockNumber')"
              >
                <i class="fa fa-ban fa-fw" />
                {{ $t('BLOCK_NUMBER') }}
              </a>
            </li>
            <li v-tooltip.left="paymentEditTooltip">
              <a
                v-if="adminViewingAsAdmin"
                role="button"
                class="btn bulk-actions-dropdown__btn"
                :disabled="outsideAdjustmentRange && !selectedCalls.length"
                @click="showBulkForm('callAdjustment')"
              >
                <i class="fa fa-usd" />
                {{ $t('ADJUST_PAYMENT') }}
              </a>
            </li>
            <li v-tooltip.left="transcribeEditTooltip">
              <a
                v-if="adminViewingAsAdmin"
                role="button"
                class="btn bulk-actions-dropdown__btn"
                :disabled="outsideAdjustmentRange && !selectedCalls.length"
                @click="showBulkForm('transcribeCalls')"
              >
                <i class="fa fa-file-text-o" />
                {{ $t('TRANSCRIBE') }}
              </a>
            </li>
          </template>
        </dropdown>

        <button
          id="reporting-call-logs__column_btn"
          class="btn btn-sm m-l-10"
          :title="$t('CUSTOMIZE_COLUMNS')"
          @click="toggleCallLogsColumnsPanel"
        >
          <i class="fa fa-columns p-r-0" />
          <span
            v-t="'CUSTOMIZE_COLUMNS'"
            class="hidden-xs p-l-5"
          />
        </button>

        <dropdown
          tag="span"
          :menu-right="true"
          class="bulk-actions-dropdown"
        >
          <btn
            size="sm"
            data-cy="button__csv_json_export_dropdown"
          >
            <span class="caret" />
          </btn>

          <template slot="dropdown">
            <li>
              <a
                role="button"
                class="btn bulk-actions-dropdown__btn"
                @click="$emit('resetColumnState')"
              >
                <i class="fa fa-undo fa-fw" />
                {{ $t('RESET_COLUMN_STATE') }}
              </a>
            </li>
            <li>
              <a
                role="button"
                class="btn bulk-actions-dropdown__btn"
                data-cy="button__export-json-settings"
                @click="$emit('exportReportJSONRequestBody')"
              >
                <i class="fa fa-download fa-fw" />
                {{ $t('EXPORT_JSON_SETTINGS') }}
              </a>
            </li>
          </template>
        </dropdown>


        <button
          v-if="canExportReport"
          class="btn btn-sm m-l-10 btn-loader btn-loader--sm"
          :disabled="noCallsData || showExportLoader"
          @click="exportReport"
        >
          <span
            class="fa fa-download p-0 m-l-4 visible-xs-inline"
            :class="{'invisible': showExportLoader}"
          />
          <span class="hidden-xs">{{ $t('EXPORT_CSV') }}</span>

          <Loader
            custom-class="size-sm m-b-5 btn-export-loader"
            :loader-class="showExportLoader ? '' : 'invisible'"
          />
        </button>
      </div>
    </div>

    <div class="ag-grid-call-logs__container">
      <a
        class="ag-grid-call-logs__collapse-right-pinned-btn"
        @click="togglePinnedColumns"
      >
        <i
          class="fa"
          :class="{'fa-chevron-left': isPinnedCollapsed, 'fa-chevron-right': !isPinnedCollapsed}"
        />
      </a>

      <BulkTagAnnotate
        v-if="showTagAnnotationBulkForm"
        :selected-calls="selectedCalls"
        :show-loader="showBulkActionLoader"
        :recent-tags="recentCallTags"
        @close="hideBulkForm"
        @annotateCalls="annotateMultipleCalls"
        @unselectCallOnBulkAction="unselectCallOnBulkAction"
      />

      <BulkBlockNumbers
        v-if="showBlockNumberBulkForm"
        :show-loader="showBulkActionLoader"
        :selected-calls="selectedCalls"
        @close="hideBulkForm"
        @bulkBlockNumbers="handleBulkBlockNumbers"
        @unselectCallOnBulkAction="unselectCallOnBulkAction"
      />

      <BulkCallAdjustmentForm
        v-if="showAdjustCallPaymentBulkForm"
        :show-loader="showBulkActionLoader"
        :selected-calls="selectedCalls"
        :is-admin="isAdmin"
        :total-calls-count="totalCallsCount"
        :pending-bulk-call-adjustment-by-logs="pendingBulkCallAdjustmentByLogs"
        @close="hideBulkForm"
        @bulkCallAdjustment="handleBulkCallAdjustment"
        @bulkCallAdjustmentByLogs="handleBulkCallAdjustmentByLogs"
        @unselectCallOnBulkAction="unselectCallOnBulkAction"
      />

      <BulkTranscriptionForm
        v-if="showTranscribeCallsBulkForm"
        :show-loader="showBulkActionLoader"
        :selected-calls="selectedCalls"
        :is-admin="isAdmin"
        :total-calls-count="totalCallsCount"
        @close="hideBulkForm"
        @bulkCallTranscription="handleBulkCallTranscription"
        @bulkCallTranscriptionByLogs="handleBulkCallTranscriptionByLogs"
        @unselectCallOnBulkAction="unselectCallOnBulkAction"
      />

      <div
        v-if="showBulkActionForm"
        class="ag-grid__overlay"
      />

      <AgGridVue
        :context="context"
        :default-col-def="defaultColDef"
        :column-defs="computedColDefs"
        :column-types="columnTypes"
        row-model-type="serverSide"
        :cache-block-size="150"
        :row-height="29.6"
        :detail-cell-renderer="detailCellRenderer"
        :master-detail="true"
        :framework-components="frameworkComponents"
        :components="components"
        :loading-cell-renderer="loadingCellRenderer"
        :loading-cell-renderer-params="loadingCellRendererParams"
        :side-bar="sideBar"
        :suppress-column-move-animation="true"
        :get-row-node-id="getRowNodeId"
        :suppress-context-menu="true"
        :block-load-debounce-millis="400"
        :row-buffer="rowBuffer"
        :suppress-column-state-events="false"
        :suppress-property-names-check="true"
        :suppress-animation-frame="true"
        :group-header-height="0"
        :get-main-menu-items="getMainMenuItems"
        overlay-no-rows-template="No calls to display. Try selecting a different date range or filter settings."
        :tooltip-show-delay="0"
        class="ag-grid--custom ag-grid--call-logs ag-theme-balham-dark"
        :class="{
          'ag-grid--call-logs--show-right-arrow' : showRightScrollArrow,
          'ag-grid--call-logs--show-left-arrow': showLeftScrollArrow,
          'ag-grid--call-logs--hide-pinned-columns': isPinnedCollapsed // Keep right scroll arrow shown when right pinned header is hidden
        }"
        row-selection="multiple"
        :row-multi-select-with-click="true"
        :suppress-row-click-selection="true"
        @firstDataRendered="onFirstDataRendered"
        @grid-ready="onGridReady"
        @sortChanged="onGridStateChange"
        @columnVisible="onColumnVisibilityChanged"
        @columnMoved="onColumnMoved"
        @columnPinned="onColumnsPinned"
        @columnResized="onColumnResized"
        @cellClicked="onCellClicked"
        @grid-size-changed="onGridSizeChanged"
        @bodyScroll="onBodyScroll"
        @cellMouseOver="onCellMouseOver"
        @cellMouseOut="onCellMouseOut"
      />
    </div>
  </div>
</template>

<script>
// ********Important*********
// This needs to be added to the first column, https://github.com/ag-grid/ag-grid/issues/2235

import Vue from 'vue';
import { AgGridVue } from '@ag-grid-community/vue';
import { isEmpty, debounce } from 'lodash';

import { MOBILE_MAX_WIDTH, isMobile } from '@/core/utilities';
import messages from './locales';
import sharedMessages from '@/core/locales';
import {
  columnTypes,
  numericTypesForCallLogs,
  handleDocumentClickListenerToCloseToolPanel,
  combineSortModelToColumnStateForNewAgGrid
} from '../../AgGridUtils';
import { reportingCallLogsGridService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { stateMapping, convertTag2AgGridColDef, getStateMappingAndRoute } from '@/modules/reporting/services/ReportingService';
import CallLogDetailCellRenderer from './CallLogDetailCellRenderer';
import RecordingCellRenderer from './RecordingCellRenderer';
import TranscriptionCellRenderer from './TranscriptionCellRenderer';
import { detailMapping } from './callLogActionFormsMixin';
import RingbaLoadingCellRenderer from '@/core/components/AgGridWrapper/ringbaLoadingCellRenderer';
import { getCallLogExpandRendererComponent, DETAIL_CONTRACTED_CLASS, DETAIL_EXPANDED_CLASS, DETAIL_EXPANDED_ICON_CLASS, DETAIL_CONTRACTED_ICON_CLASS } from './CallLogExpandCellRenderer';
import {copyText} from '@/core/directives/CopyToClipboard';
import BulkTagAnnotate from './BulkActions/BulkTagAnnotate';
import BulkBlockNumbers from './BulkActions/BulkBlockNumbers';
import BulkCallAdjustmentForm from './BulkActions/BulkCallAdjustmentForm';
import BulkTranscriptionForm from './BulkActions/BulkTranscriptionForm';
import Loader from '@/core/components/Loader';
import { remove } from 'lodash';

const NEW_TAB_ICON_TOOLTIP = 'Open in new tab';
const FILTER_ICON_TOOLTIP = 'Filter';
const COPY_ICON_TOOLTIP = 'Copy';
const ACTION_COLUMN_FIELD_NAME = 'action';
const ACTION_COLUMN_FIELD_WIDTH_ON_MOBILE = 80;
const LIMIT_BULK_ACTION_COUNT = 10;
const MIN_SCREEN_WIDTH_FOR_NON_ADMIN_TO_AUTO_SIZE = 800;
const MAX_ADJUSTMENT_COUNT = 5000;

const callConversionStatusForBuyerCssMapping = {
  'requested': {
    color: 'yellow',
    tooltip: 'Dispute Pending'
  },
  'approved': {
    color: 'green',
    tooltip: 'Dispute Approved. Click to dispute again'
  },
  'rejected': {
    color: 'red',
    tooltip: 'Dispute Rejected. Click to dispute again'
  }
};

const defaultColDef = {
  sortable: true,
  flex: 1,
  resizable:true,
  menuTabs: ['generalMenuTab'],
};

let gridApi, gridColumnApi;
let handleDocumentClick;
let windowScrollListener;

function checkIfSortedColumnsAreNotVisible(displayedColumns) {
  return !displayedColumns.some(col => col.sort);
}


function getActionsColumnDef(context) {
  const actionsColumnDef = {
    headerName: 'Actions',
    field: ACTION_COLUMN_FIELD_NAME,
    colId: ACTION_COLUMN_FIELD_NAME,
    pinned: 'right',
    suppressMenu: true,
    resizable: false,
    lockVisible: true,
    lockPinned: true,
    sortable: false,
    minWidth: 75,
    flex: null,
    cellRenderer: function actionColumnCellRenderer(params) {
      const showNumberBlockAction = context.showNumberBlockAction;
      const showDisputeAction = context.showDisputeAction;
      const showAnnotateAction = context.showAnnotateAction;

      let numberBlockAction = '';
      let disputeAction = '';
      let annotateAction = '';

      if (showNumberBlockAction) {
        const blockedNumberStatus = this.blockedNumbersHashMap[params.data?.inboundPhoneNumber];
        const isNumberOnBlockList = blockedNumberStatus
                                  && (!blockedNumberStatus.campaignId || (blockedNumberStatus.campaignId === params.data?.campaignId));
        const titleTooltip = isNumberOnBlockList ? this.unblockNumberTitle : this.blockNumberTitle;
        numberBlockAction = `<a
                    title="${titleTooltip}"
                    class="btn btn-function block-number-btn"
                  >
                    <i class="fa fa-ban ${isNumberOnBlockList ? 'text-danger': ''}"></i>
                  </a>`;
      }

      if (showDisputeAction) {
        let disputeActionStatusCss = '';
        let disableCallAdjustment = '';
        let disputeTitle = context.adjustTitle;
        let converted = params.data?.hasConverted;

        if (context.isBuyer) {
          converted = params.data?.buyerHasConverted;
          const conversionAdjustmentStatus = params.data?.buyerConversionAdjustmentStatus?.toLowerCase();
          const conversionAdjustmentStatusMapping = callConversionStatusForBuyerCssMapping[conversionAdjustmentStatus];

          disputeActionStatusCss = conversionAdjustmentStatusMapping?.color || '';
          disputeTitle = conversionAdjustmentStatusMapping?.tooltip || disputeTitle;

          disableCallAdjustment = conversionAdjustmentStatus === 'requested' || !converted ? 'disabled' : '';
        }


        const disputeIcon = context.isBuyer ? 'fa fa-comments-o' : 'fa fa-usd';
        disputeAction = `<a
                  title="${disputeTitle}"
                  class="btn btn-function m-l-5 adjust-call-payment-btn ${disputeActionStatusCss}"
                  ${disableCallAdjustment}
                >
                  <i class="${disputeIcon}"></i>
                </a>`;
      }

      if (showAnnotateAction) {
        annotateAction = `<a
                title="${this.annotateTitle}"
                class="btn btn-function m-l-5 annotate-call-btn"
              >
                <i class="fa fa-pencil"></i>
              </a>`;
      }

      return `<div>
              ${numberBlockAction}

              ${annotateAction}

              ${disputeAction}
            </div>`;
    }.bind(context)
  };
  return actionsColumnDef;
}

function handleWindowScroll() {
  const documentBottomOffset = 10;

  if ((window.innerHeight + window.scrollY) >= (document.body.scrollHeight - documentBottomOffset)) {
    this.userScrolledToBottom = true;
  } else {
    this.userScrolledToBottom = false;
  }
}

function buildTooltipElement(x, y, showOpenNewTabLink, newTabCallback, filterCallback, copyCallback) {
  const tooltipElement = document.createElement('div');
  tooltipElement.classList.add('tooltip', 'tooltip-black', 'tooltip-inline-new-tab', 'top', 'fade', 'in');
  tooltipElement.style.left = `${x}px`;
  tooltipElement.style.top = `${y}px`;

  const tooltipArrowElement = document.createElement('div');
  tooltipArrowElement.classList.add('tooltip-arrow');
  tooltipElement.appendChild(tooltipArrowElement);

  const tooltipInnerElement = document.createElement('div');
  tooltipInnerElement.classList.add('tooltip-inner');

  const externalLinkElement = document.createElement('a');
  externalLinkElement.setAttribute('title', NEW_TAB_ICON_TOOLTIP);
  externalLinkElement.classList.add('btn', 'btn-function', 'external-link-btn', 'm-r-5');

  if (!showOpenNewTabLink) {
    externalLinkElement.style.display = 'none';
  }

  const externalLinkIconElement = document.createElement('i');
  externalLinkIconElement.classList.add('fa', 'fa-external-link');


  const filterElement = document.createElement('a');
  filterElement.classList.add('btn', 'btn-function', 'filter-btn', 'm-b-2');
  filterElement.setAttribute('title', FILTER_ICON_TOOLTIP);
  const filterIconElement = document.createElement('i');
  filterIconElement.classList.add('fa', 'fa-filter');

  const copyElement = document.createElement('a');
  copyElement.classList.add('btn', 'btn-function', 'copy-value-btn', 'm-b-2', 'm-l-5');
  copyElement.setAttribute('title', COPY_ICON_TOOLTIP);
  const copyIconElement = document.createElement('i');
  copyIconElement.classList.add('typcn', 'typcn-tabs-outline', 'p-0');

  externalLinkElement.appendChild(externalLinkIconElement);
  filterElement.appendChild(filterIconElement);
  copyElement.appendChild(copyIconElement);

  tooltipInnerElement.appendChild(externalLinkElement);
  tooltipInnerElement.appendChild(filterElement);
  tooltipInnerElement.appendChild(copyElement);

  tooltipElement.appendChild(tooltipInnerElement);

  tooltipElement.addEventListener('click', (ev) => {
    const path = ev.path || (ev.composedPath ? ev.composedPath() : undefined);

    if (path?.indexOf(externalLinkElement) > -1) {
      newTabCallback();
    } else if (path?.indexOf(filterElement) > -1) {
      filterCallback();
    } else if (path?.indexOf(copyElement) > -1) {
      copyCallback();
    }
  });

  tooltipElement.addEventListener('mouseleave', (e) => {
    if (e.relatedTarget?.closest('.ag-cell')) {
      return;
    } else {
      e.target.style.display = 'none';
    }
  });

  return tooltipElement;
}

function positionTooltipElement(tooltipElement, x, y,showOpenNewTabLink) {
  const externalLinkElement = tooltipElement.querySelector('.external-link-btn');

  if (!showOpenNewTabLink) {
    externalLinkElement.style.display = 'none';
  } else {
    externalLinkElement.style.display = 'inline';
  }

  tooltipElement.style.left = `${x}px`;
  tooltipElement.style.top = `${y}px`;
  tooltipElement.style.display = 'block';
}

function handleRightPinnedHeaderClick(e) {
  if (e.offsetX < 0) {
    const horizontalScroll = gridApi.gridCore.getRefElement('eBodyHorizontalScrollViewport');
    horizontalScroll.style.scrollBehavior = 'smooth';
    horizontalScroll.scrollLeft = horizontalScroll.scrollWidth;
    horizontalScroll.style.scrollBehavior = '';
  }
}

function handleLeftPinnedHeaderClick(e) {
  if (e.offsetX > e.currentTarget.offsetWidth) {
    const horizontalScroll = gridApi.gridCore.getRefElement('eBodyHorizontalScrollViewport');
    horizontalScroll.style.scrollBehavior = 'smooth';
    horizontalScroll.scrollLeft = 0;
    horizontalScroll.style.scrollBehavior = '';
  }
}

export default {
  name: 'ReportingCallLogsGrid',
  components: {
    AgGridVue,
    BulkTagAnnotate,
    BulkBlockNumbers,
    BulkCallAdjustmentForm,
    BulkTranscriptionForm,
    Loader,
  },
  props: {
    colDefsLoadedFromApi: {
      type: Boolean,
      default: false
    },
    columnDefs: {
      type: Array,
      default() {
        return [];
      }
    },
    gridState: {
      type: Object,
      default() {
        return {};
      }
    },
    showLoader: {
      type: Number,
      default: 0
    },
    hideLoader: {
      type: Number,
      default: 0
    },
    showCallLogActionLoaderStatus: {
      type: Object,
      default() {
        return {};
      }
    },
    callLogActionFormVisibilityStatus: {
      type: Object,
      default() {
        return {};
      }
    },
    campaigns: {
      type: Array,
      default() {
        return [];
      }
    },
    refreshGridData: {
      type: Number,
      default: 0
    },
    role: {
      type: String,
      default: 'error'
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    isReadOnlyReportUser: {
      type: Boolean,
      default: false
    },
    isBuyer: {
      type: Boolean,
      default: false
    },
    recentCallTags: {
      type: Array,
      default() {
        return [];
      }
    },
    showAudioPlayerForCall: {
      type: Object,
      default() {
        return {};
      }
    },
    callLogDetailsMapping: {
      type: Object,
      default() {
        return {};
      }
    },
    showCallLogsColumnsPanel: {
      type: Number,
      default: 0
    },
    blockedNumbersHashMap: {
      type: Object,
      default() {
        return {};
      }
    },
    showBulkActionLoader: {
      type: Boolean,
      default: false
    },
    hideBulkActionForm: {
      type: Number,
      default: 0
    },
    timeZoneId: {
      type: String,
      default: ''
    },
    canDisputeConversions: {
      type: Boolean,
      default: false
    },
    disputeCallStatus: {
      type: Object,
      default() {
        return {};
      }
    },
    canExportReport: {
      type: Boolean,
      default: false
    },
    openColumnsToolPanel: {
      type: Boolean,
      default: false
    },
    openTourCallDetails: {
      type: Boolean,
      default: false
    },
    showExportLoader: {
      type: Boolean,
      default: false
    },
    pendingBulkCallAdjustmentByLogs: {
      type: Boolean,
      default: false
    }
  },
  data() {
    // Non-reactive data on `this`
    this.tooltipStateMapping = {};
    this.userScrolledToBottom = false;
    this.firstDataRenderedFlag = false;

    try {
      this.blockNumberTitle = this.$i18n.t('BLOCK_NUMBER');
      this.unblockNumberTitle = this.$i18n.t('UNBLOCK_NUMBER');
      this.annotateTitle = this.$i18n.t('ANNOTATE_CALL');
      this.adjustTitle = this.isBuyer ? this.$i18n.t('DISPUTE_CALL_CONVERSION') : this.$i18n.t('ADJUST_CALL_PAYMENT');
    } catch (error) {
      this.blockNumberTitle = 'Block Number';
      this.unblockNumberTitle = 'Unblock Number';
      this.annotateTitle = 'Add Tag Annotation';
      this.adjustTitle = 'Adjust Call Payments';
    }
    // TODO: Check performance issue with bind
    this.actionsColumnDef = Object.freeze(getActionsColumnDef(this));

    return {
      context: null,
      noCallsData: true,
      limitBulkRowCount: Object.freeze(LIMIT_BULK_ACTION_COUNT),
      showRightScrollArrow: false,
      showLeftScrollArrow: false,
      isPinnedCollapsed: false,
      showBulkActionForm: undefined,
      totalCallsCount: null,
      selectedCalls: [],
      customTagColDefs: [],
      detailCellRenderer: 'callLogDetailCellRenderer',
      gridStateId: performance.now(),
      defaultColDef: Object.freeze({
        ...defaultColDef,
        cellClass: this.getCellClass,
      }),
      frameworkComponents: Object.freeze({
        callLogDetailCellRenderer: CallLogDetailCellRenderer,
        recordingCellRenderer: RecordingCellRenderer,
        transcriptionCellRenderer: TranscriptionCellRenderer,
        customLoadingCellRenderer: RingbaLoadingCellRenderer
      }),
      components: Object.freeze({
        callLogExpandDetailCellRenderer: getCallLogExpandRendererComponent(this),
      }),
      loadingCellRenderer: Object.freeze('customLoadingCellRenderer'),
      loadingCellRendererParams: Object.freeze({
        loadingMessage: 'Loading...'
      }),
      sideBar: Object.freeze({
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressSyncLayoutWithGrid: true,
              suppressColumnSelectAll: true,
              contractColumnSelection: false,
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
              suppressPivots: true,
            }
          }
        ]
      }),
      expandedDetailRowNodes: {},
      tabIndexCacheForDetailOpenCalls: {},
      rowBuffer: 20,
      columnTypes: Object.freeze({
        ...columnTypes,
        boolean: {
          valueFormatter({ value }) {
            if (typeof value === 'undefined') {
              return '';
            }

            if (value === 0 || value === false) {
              return 'No';
            } else if (value === 1 || value === true) {
              return 'Yes';
            }

            return '';
          },

        }
      }),
    };
  },

  computed: {
    computedColDefs() {
      const tagColumnDefs = this.columnDefs.find((groupColDef) => {
        return groupColDef.headerName?.toLowerCase() === 'tags';
      });

      if (tagColumnDefs) {
        tagColumnDefs.children = tagColumnDefs?.children.concat(this.customTagColDefs);
      }

      if (!this.actionPermissions) {
        return this.columnDefs;
      }

      return this.columnDefs.concat(this.actionsColumnDef);
    },

    generalEditTooltip() {
      const limitBulkRowCount = this.limitBulkRowCount;
      return this.selectedCalls.length ? this.$i18n.t('EDIT_SELECTED_CALLS') : this.$i18n.t('EDIT_CALLS_GENERAL', {limitBulkRowCount});
    },

    paymentEditTooltip() {
      const limitBulkRowCount = this.limitBulkRowCount;
      return this.outsideAdjustmentRange && !this.selectedCalls.length ? this.$i18n.t('EDIT_CALLS_COUNT_EXCEEDED', {MAX_ADJUSTMENT_COUNT}) : this.selectedCalls.length ? this.$i18n.t('EDIT_SELECTED_CALLS') : this.$i18n.t('EDIT_CALLS_PAYMENT');
    },

    transcribeEditTooltip() {
      const limitBulkRowCount = this.limitBulkRowCount;
      return this.outsideAdjustmentRange && !this.selectedCalls.length ? this.$i18n.t('EDIT_CALLS_COUNT_EXCEEDED', {MAX_ADJUSTMENT_COUNT}) : this.selectedCalls.length ? this.$i18n.t('TRANSCRIBE_SELECTED_CALLS') : this.$i18n.t('TRANSCRIBE_CALLS');
    },

    outsideAdjustmentRange() {
      const hasSelectedCalls = this.selectedCalls.length > 0;
      const hasTooManyCalls = (hasSelectedCalls ? this.selectedCalls.length : this.totalCallsCount) > MAX_ADJUSTMENT_COUNT;

      return this.totalCallsCount < 1 || hasTooManyCalls;
    },

    showTagAnnotationBulkForm() {
      return this.showBulkActionForm === 'tagAnnotate';
    },

    showBlockNumberBulkForm() {
      return this.showBulkActionForm === 'blockNumber';
    },

    showAdjustCallPaymentBulkForm() {
      return this.showBulkActionForm === 'callAdjustment';
    },

    showTranscribeCallsBulkForm() {
      return this.showBulkActionForm === 'transcribeCalls';
    },

    adminViewingAsAdmin() {
      return this.isAdmin && this.role === 'admin';
    },

    // TODO: These action permissions should be coming from Vuex rather than this component
    showNumberBlockAction() {
      return this.adminViewingAsAdmin;
    },

    showDisputeAction() {
      return this.adminViewingAsAdmin || (this.isBuyer && this.canDisputeConversions);
    },

    showAnnotateAction() {
      return this.adminViewingAsAdmin;
    },

    actionPermissions() {
      return this.showNumberBlockAction || this.showAnnotateAction || this.showDisputeAction;
    }

  },

  watch: {
    refreshGridData(newValue, oldValue) {
      if (newValue !== oldValue && gridApi) {
        this.refreshGrid();
      }
    },
    showLoader() {
      gridApi.showLoadingOverlay();
    },
    hideLoader() {
      gridApi.hideOverlay();
    },
    gridState: {
      immediate: true,
      handler(newValue) {
        if (!isEmpty(newValue) && newValue.id !== this.gridStateId) {
          this.gridStateId = newValue.id || performance.now();
          this.setGridState(newValue);
        }
      }
    },
    columnDefs() {
      Vue.nextTick(() => {
        this.setGridState(this.gridState);

        setTimeout(() => {
          if (this.role !== 'admin' && gridApi && window.innerWidth > MIN_SCREEN_WIDTH_FOR_NON_ADMIN_TO_AUTO_SIZE) {
            this.togglePinnedColumns();
            setTimeout(() => {
              this.togglePinnedColumns();
            }, 1);
          }
        }, 1);
      });
    },

    blockedNumbersHashMap() {
      // Refresh Action column
      this.refreshActionColumn();
    },

    hideBulkActionForm() {
      this.showBulkActionForm = undefined;
    },

    actionPermissions() {
      if (this.firstDataRenderedFlag) {
        this.refreshActionColumn();
      }
    },

    disputeCallStatus(newValue) {
      const [callId] = Object.keys(newValue);
      const rowNode = gridApi.getRowNode(callId);

      const data = rowNode.data;
      data.buyerConversionAdjustmentStatus = newValue[callId];

      rowNode.setData(data);
      this.refreshActionColumn();
    },

    openColumnsToolPanel(newValue) {
      if (newValue) {
        this.toggleCallLogsColumnsPanel();
      }
    },

    openTourCallDetails(newValue) {
      var row = gridApi.getDisplayedRowAtIndex(0);

      if (row) {
        this.showCallLogDetail(row, newValue);
      }
    },
  },

  beforeMount () {
    this.context = {
      componentParent: this
    };
  },

  destroyed () {
    document.removeEventListener('click', handleDocumentClick, true);
    handleDocumentClick = null;
    gridApi = null;
    gridColumnApi = null;
    document.querySelector(`.tooltip`)?.remove();
    window.removeEventListener('scroll', windowScrollListener);
  },

  methods: {
    onFirstDataRendered() {
      this.firstDataRenderedFlag = true;
    },
    onGridReady(params) {
      gridApi = params.api;
      gridColumnApi = params.columnApi;

      if (this.colDefsLoadedFromApi) {
        this.setGridState(this.gridState);
      }

      reportingCallLogsGridService.setGridCallback(this.onGridCallback);
      params.api.setServerSideDatasource(reportingCallLogsGridService);
      params.api.hideOverlay();

      const outsideClickListener = handleDocumentClickListenerToCloseToolPanel(gridApi);

      handleDocumentClick = function (ev) {
        outsideClickListener(ev, document.getElementById('reporting-call-logs__column_btn'));
      };

      document.addEventListener('click', handleDocumentClick, true);

      // Add click listeners for the right and left scroll icons
      const rightPinnedHeader = gridApi.gridCore.getRefElement('ePinnedRightHeader');
      rightPinnedHeader.addEventListener('click', handleRightPinnedHeaderClick);

      const leftPinnedHeader = gridApi.gridCore.getRefElement('ePinnedLeftHeader');
      leftPinnedHeader.addEventListener('click', handleLeftPinnedHeaderClick);

      // Grid locking - Start
      windowScrollListener = handleWindowScroll.bind(this);
      window.addEventListener('scroll', windowScrollListener);

      // https://stackoverflow.com/questions/4770025/how-to-disable-scrolling-temporarily
      // The scroll event itself is not cancelable, so we cancel these events
      const gridMainBody = gridApi.gridCore.getRefElement('eBodyViewport');
      gridMainBody.addEventListener('wheel', this.gridMainBodyScrollListener); // modern desktop
      gridMainBody.addEventListener('touchmove', this.gridMainBodyScrollListener); // mobile
      // Grid locking - End
    },

    gridMainBodyScrollListener(e) {
      if (!this.userScrolledToBottom && (e.deltaY > 0)) {
        window.scrollBy(0, e.deltaY);
        e.preventDefault();
      }
    },

    unselectCallOnBulkAction(call) {
      remove(this.selectedCalls, (c) => {
        return c == call;
      });

      const node = gridApi.getRowNode(call.inboundCallId);
      node.setSelected(false);
    },

    showBulkForm(action) {
      if (!this.selectedCalls.length && (action === 'tagAnnotate' || action === 'blockNumber')) {
        return;
      }
      if (this.selectedCalls.length > LIMIT_BULK_ACTION_COUNT && (action === 'tagAnnotate' || action === 'blockNumber')) {
        utilities.showToaster('info', this.$i18n.t('EDIT_CALLS_BULK_ACTION_LIMIT', {LIMIT_BULK_ACTION_COUNT}));
        return;
      }
      if (this.outsideAdjustmentRange && (action === 'callAdjustment' || action === 'transcribeCalls')) {
        return;
      }

      this.showBulkActionForm = action;

      gridApi?.getSelectedNodes()?.forEach(row => this.closeForm(row));
    },

    hideBulkForm() {
      this.showBulkActionForm = undefined;
    },

    onCellMouseOver(params) {
      // Add tooltips over cells with links and filters both
      if (params.value && params.event.target.classList.contains('filterable-cell')) {
        const cellPosition = `${params.rowIndex}-${params.column.colId}`;
        const existingTooltipElement = document.querySelector(`.tooltip`);

        if (this.tooltipStateMapping?.cellPosition === cellPosition) {
          return;
        }

        const {stateMappingCopy, routeParams} = getStateMappingAndRoute(params.column.colId, params.data );
        const showOpenNewTabLink = !!stateMappingCopy;

        const isTag = params.colDef.type === 'tag';
        const parts = params.column.colId.split(':');

        this.tooltipStateMapping = {
          cellPosition,
          rowIndex: params.rowIndex,
          colId: params.column.colId,
          value: params.value,
          filterGroupName: params.column.colDef.filterGroupName,
          type: params.colDef.type,
          ...stateMappingCopy,
          routeParams,
          isTag,
          originalTag: isTag ? {tagType: parts[1], tagName: parts[2]} : null
        };

        const eventTarget = params.event.target;
        const cellElement = eventTarget.classList.contains('ag-cell-value') ? eventTarget : eventTarget.closest('.ag-cell-value');
        const scrollTop = document.documentElement.scrollTop;
        const { top, height } = cellElement.getBoundingClientRect();
        const finalLeftPosition = params.event.clientX - document.documentElement.getBoundingClientRect().left - 20; //left + scrollLeft + params.event.offsetX;

        if (existingTooltipElement) {
          positionTooltipElement(
            existingTooltipElement,
            finalLeftPosition,
            top + scrollTop - height,
            showOpenNewTabLink
          );
          return;
        }

        document.body.appendChild(buildTooltipElement(
          finalLeftPosition,
          top + scrollTop - height,
          showOpenNewTabLink,
          this.onClickNewTabIconInCellTooltip,
          this.onClickFilterIconInCellTooltip,
          this.onClickCopyIconInCellTooltip
        ));
      }

      if (stateMapping[params.column.colId] && !params.value) {
        this.hideCellTooltip();
      }
    },

    onCellMouseOut(params) {
      if (params.event.relatedTarget?.classList.contains('tooltip-inline-new-tab')
          || params.event.relatedTarget?.closest('.tooltip-inline-new-tab')) {
        return;
      }

      this.hideCellTooltip();
    },

    onClickNewTabIconInCellTooltip() {
      if (this.tooltipStateMapping) {
        utilities.openInNewTab({
          routeName: this.tooltipStateMapping.routeName,
          paramsObj: {
            ...this.tooltipStateMapping.routeParams
          }
        });
      }
    },

    onClickFilterIconInCellTooltip() {
      if (this.tooltipStateMapping) {
        this.addFilter({
          colId: this.tooltipStateMapping.colId,
          value: this.tooltipStateMapping.value,
          filterGroupName: this.tooltipStateMapping.filterGroupName,
          type: this.tooltipStateMapping.type,
          isTag: this.tooltipStateMapping.isTag,
          originalTag: this.tooltipStateMapping.originalTag
        });
      }
    },

    onClickCopyIconInCellTooltip() {
      if (this.tooltipStateMapping) {
        copyText(this.tooltipStateMapping.value);
      }
    },

    onBodyScroll: debounce(function onBodyScroll(e) {
      if (e.direction === 'horizontal') {
        this.hideOrShowHorizontalScroll();
      }

      this.hideCellTooltip();
    }, 600),

    hideCellTooltip() {
      if (this.tooltipStateMapping) {
        const tooltipElement = document.querySelector(`.tooltip`);

        if (tooltipElement) {
          tooltipElement.style.display = 'none';
          this.tooltipStateMapping = undefined;
        }
      }
    },

    hideOrShowHorizontalScroll() {
      const horizontalScroll = gridApi?.gridCore?.getRefElement('eBodyHorizontalScrollViewport');

      if(!horizontalScroll) {
        this.showRightScrollArrow = false;
        this.showLeftScrollArrow = false;
        return;
      }

      // Stop showing the right arrow button before 10px
      if ((horizontalScroll.clientWidth + horizontalScroll.scrollLeft) < (horizontalScroll.scrollWidth - 10)) {
        this.showRightScrollArrow = true;
      } else {
        this.showRightScrollArrow = false;
      }

      if (horizontalScroll.scrollLeft > 30) {
        this.showLeftScrollArrow = true;
      } else {
        this.showLeftScrollArrow = false;
      }
    },

    onGridCallback(callLogs, totalCount) {
      this.totalCallsCount = totalCount;
      this.$emit('onGridDataLoaded');
      if (gridApi) {
        if (callLogs?.length === 0) {
          this.noCallsData = true;
          gridApi.showNoRowsOverlay();
        } else {
          this.noCallsData = false;
          gridApi.hideOverlay();
        }
      }
    },
    onGridSizeChanged(params) {
      this.hideOrShowHorizontalScroll();

      if (params.clientWidth <= MOBILE_MAX_WIDTH) {
        // Unpin the columns on the right to make space for other columns on mobile view
        this.unPinRightColumns();
        gridColumnApi.setColumnWidth(ACTION_COLUMN_FIELD_NAME, ACTION_COLUMN_FIELD_WIDTH_ON_MOBILE);
      }
    },
    unPinRightColumns() {
      const columnsToUnpin = this.gridState.columnState.filter((columnState) => {
        return columnState.pinned === 'right';
      }).map((columnState) => columnState.colId);

      gridColumnApi.setColumnsPinned(columnsToUnpin, null);
    },
    getRowNodeId(callLog) {
      return callLog.inboundCallId;
    },
    onGridStateChange(resetData = false) {
      if (this.columnDefs?.length && this.colDefsLoadedFromApi) {
        this.gridStateId = performance.now();
        this.$emit('gridStateChange', { gridState: this.getGridState(this.gridStateId), resetData, isMobile: isMobile });
      }
    },
    getGridState(id) {
      return {
        filterModel: gridApi?.getFilterModel(),
        columnState: gridColumnApi?.getColumnState(),
        id
      };
    },

    onColumnsPinned() {
      if (this.isPinnedCollapsed) {
        this.showHiddenPinnedColumns();
        this.isPinnedCollapsed = false;
      }

      this.onGridStateChange();
    },

    onColumnVisibilityChanged(e) {
      if (checkIfSortedColumnsAreNotVisible(e.columnApi.getAllDisplayedColumns())) {
        gridColumnApi.applyColumnState({
          state: [{
            colId: 'callDt',
            sort: 'desc',
            sortIndex: 0,
            hide: false,
            pinned: 'left'
          }]
        });
      }

      this.onGridStateChange();

      this.hideOrShowHorizontalScroll();

      if (e.columns?.some(col => col.visible && col.userProvidedColDef?.type === 'tag')) {
        // TODO: this could be smarter, ie. if the data is already load it just show it.
        this.refreshGrid();
      }
    },
    onColumnMoved: debounce(function onColumnMoved() {
      this.onGridStateChange();
    }, 1000),
    onColumnResized: debounce(function onColumnResized() {
      this.hideOrShowHorizontalScroll();
      this.onGridStateChange();
    }, 1000),
    onCellClicked(e) {
      const targetNode = e.event.target;
      const cssClassList = e.event.target.classList;

      if (cssClassList.contains('filterable-cell')) {
        const parts = e.column.colId.split(':');
        const isTag = e.colDef.type === 'tag';
        this.addFilter({
          colId: e.column.colId,
          value: e.value,
          filterGroupName: e.column.colDef.filterGroupName,
          type: e.colDef.type,
          isTag,
          originalTag: isTag ? {tagType: parts[1], tagName: parts[2]} : null
        });
        return;
      }

      if (cssClassList.contains('group-expand-cell')) {
        this.showDetailCellRenderer(e, cssClassList);
        return;
      }

      const parentNode = targetNode.parentNode;
      const parentNodeCssClassList = targetNode.parentNode.classList;

      if (cssClassList.contains('block-number-btn') || parentNodeCssClassList.contains('block-number-btn')) {
        this.blockUnblockNumber(e);
        return;
      } else if (cssClassList.contains('annotate-call-btn') || parentNodeCssClassList.contains('annotate-call-btn')) {
        this.showCallAnnotateForm(e.node);
        return;
      } else if (cssClassList.contains('adjust-call-payment-btn') || parentNodeCssClassList.contains('adjust-call-payment-btn')) {
        // Don't do anything if button is disabled
        if (targetNode.getAttribute('disabled') !== null || parentNode.getAttribute('disabled') !== null) {
          return;
        }

        this.showCallPaymentAdjustmentForm(e.node);
        return;
      }

      if (cssClassList.contains('group-expand-cell') || parentNodeCssClassList.contains('recording-cell__btn') || parentNodeCssClassList.contains('transcription-cell__btn')) {
        return;
      }

      this.toggleRowSelection(e.node);
    },

    toggleRowSelection(node) {
      let select = true;
      if (node.selected) {
        select = false;
      }

      node.setSelected(select);

      this.selectedCalls = gridApi.getSelectedRows();
    },

    blockUnblockNumber(e) {
      const blockedNumberStatus = this.blockedNumbersHashMap[e.node.data.inboundPhoneNumber];
      const isNumberOnBlockList = blockedNumberStatus && (!blockedNumberStatus.campaignId || (blockedNumberStatus.campaignId === e.node.data.campaignId));

      if (isNumberOnBlockList) {
        this.$emit('unblockNumber', blockedNumberStatus);
        this.closeForm(e.node);
      } else {
        this.showBlockNumberForm(e.node);
      }
    },

    addFilter({ colId, value, filterGroupName, type, isTag, originalTag }) {
      this.$emit('filterByColumn', [{
        column: colId,
        value: value,
        filterGroupName: filterGroupName,
        type: type,
        isTag: !!isTag,
        originalTag,
      }]);
    },

    addTagAsColumn(tag) {
      const colId = `tag:${tag.type}:${tag.name}`;
      const groupedTagsColDefs = this.columnDefs.find((groupColDef) => groupColDef?.headerName?.toLowerCase() === 'tags');
      const existingColDef = groupedTagsColDefs?.children.find(column => column.colId === colId);

      if (!existingColDef && !this.customTagColDefs.find(column => column.colId === colId)) {
        this.customTagColDefs.push(convertTag2AgGridColDef({
          tagType: tag.type,
          tagName: tag.name
        }, { minWidth: 150, flex: 2 }));
      }

      setTimeout(() => {
        gridColumnApi.setColumnVisible(colId, true);
      }, 0);
    },

    showDetailCellRenderer(e, targetCssClassList) {
      if (targetCssClassList.contains('group-expand-cell--contracted')) {
        this.showCallLogDetail(e.node, true);
        targetCssClassList.replace('group-expand-cell--contracted', 'group-expand-cell--expanded');
        targetCssClassList.replace('ag-icon-small-down', 'ag-icon-small-up');
      } else {
        this.showCallLogDetail(e.node, false);
        targetCssClassList.replace('group-expand-cell--expanded', 'group-expand-cell--contracted');
        targetCssClassList.replace('ag-icon-small-up', 'ag-icon-small-down');
      }
    },

    showBlockNumberForm(rowNode) {
      this.closeForm(rowNode);
      rowNode.data.showForm = detailMapping['blockNumber'];
      this.expandDetail(rowNode, rowNode.data.showForm);
    },
    showCallAnnotateForm(rowNode) {
      this.closeForm(rowNode);
      rowNode.data.showForm = detailMapping['annotate'];
      this.expandDetail(rowNode, rowNode.data.showForm);
    },
    showCallPaymentAdjustmentForm(rowNode) {
      this.closeForm(rowNode);
      rowNode.data.showForm = detailMapping['adjustPayment'];
      this.expandDetail(rowNode, rowNode.data.showForm);
    },
    closeForm(rowNode) {
      rowNode.data.showForm = undefined;
      rowNode.setExpanded(false);
      Vue.set(this.expandedDetailRowNodes, rowNode.id, undefined);

      this.changeExpandContractDetailIcon(rowNode, false);
    },
    expandDetail(rowNode, showForm, tab) {
      this.$emit('changeCallLogActionFormVisibilityStatus', { callId: rowNode.id, status: true });

      rowNode.setExpanded(true);

      Vue.set(this.expandedDetailRowNodes, rowNode.id, {
        showForm,
        tab: tab
      });

      this.changeExpandContractDetailIcon(rowNode, true);
    },
    changeExpandContractDetailIcon(rowNode, expand) {
      const cellRendererInstance = gridApi.getCellRendererInstances({
        columns: ['callDt'],
        rowNodes: [rowNode]
      })[0];

      if (!cellRendererInstance) {
        return;
      }
      const iconElement = cellRendererInstance.eGui.getElementsByClassName('group-expand-cell')[0];

      if (expand) {
        iconElement.classList.replace(DETAIL_CONTRACTED_CLASS, DETAIL_EXPANDED_CLASS);
        iconElement.classList.replace(DETAIL_CONTRACTED_ICON_CLASS, DETAIL_EXPANDED_ICON_CLASS);
      } else {
        iconElement.classList.replace(DETAIL_EXPANDED_CLASS, DETAIL_CONTRACTED_CLASS);
        iconElement.classList.replace(DETAIL_EXPANDED_ICON_CLASS, DETAIL_CONTRACTED_ICON_CLASS);
      }
    },
    blockNumber(number, callId) {
      this.$emit('blockNumber', { number, callId: callId });
    },
    annotateCall(tag, callId) {
      this.$emit('annotateCall', { tag, callId: callId });
    },
    callPaymentDispute(data, callId) {
      this.$emit('callPaymentDispute', { data, callId: callId });
    },
    showCallLogDetail(rowNode, showDetail, tab = '') {
      if (showDetail) {
        this.$emit('getCallDetails', rowNode.id);
        this.expandDetail(rowNode, detailMapping['detail'], tab);
      } else {
        this.closeForm(rowNode);
      }
    },
    setTabIndexCacheForCall(callId, tabIndex) {
      this.tabIndexCacheForDetailOpenCalls[callId] = tabIndex;
    },
    setDataCacheForActionForm(callId, data) {
      try {
        this.expandedDetailRowNodes[callId] = {
          ...this.expandedDetailRowNodes[callId],
          data
        };
      } catch (error) {
        //
      }
    },
    showTranscriptionDetails(callId) {
      const rowNode = gridApi.getRowNode(callId);
      this.showCallLogDetail(rowNode, true, 'transcription');
    },
    showAudioPlayer(rowNodeData) {
      this.$emit('showAudioPlayer', {
        recordingUrl: rowNodeData.recordingUrl,
        inboundNumber: rowNodeData.inboundPhoneNumber,
        callId: rowNodeData.inboundCallId,
        hasTranscription: rowNodeData.hasTranscription
      });
    },
    stopAudioPlayer(rowNodeData) {
      this.$emit('stopAudioPlayer', {
        recordingUrl: rowNodeData.recordingUrl,
        inboundNumber: rowNodeData.inboundPhoneNumber,
        callId: rowNodeData.inboundCallId
      });
    },
    setGridState(gridState) {

      if (isEmpty(gridState) || !gridApi || !gridColumnApi || !this.colDefsLoadedFromApi) {
        return;
      }

      if (gridState.columnState) {
        
        const columnState = combineSortModelToColumnStateForNewAgGrid(gridState.sortModel, gridState.columnState);

        // We need this so when a user's permission to perform user actions are revoked and then given back
        // we set the action state manually
        if (this.actionPermissions && !columnState.find(col => col.colId === 'action')) {
          columnState.push({
            colId: 'action',
            hide: false,
            width: 75,
            minWidth: 75,
            pinned: 'right'
          });
        }

        gridColumnApi.applyColumnState({
          state: columnState,
          applyOrder: true,
          defaultState: {
            hide: true
          }
        });
      }

      if (gridState.filterModel) {
        gridApi.setFilterModel(gridState.filterModel);
      }

    },

    getCellClass(params) {
      if (params) {
        const classes = [];

        if (params.colDef) {
          if (numericTypesForCallLogs[params.colDef.type]) {
            classes.push('ag-right-aligned-cell');
          }
        }
        return classes;
      }
    },
    getMainMenuItems({ column, columnApi, defaultItems }) {
      defaultItems.splice(defaultItems.indexOf('resetColumns'), 1);

      return [
        {
          name: 'Hide column',
          action() {
            columnApi.setColumnVisible(column.colId, false);
          },
          icon: '<span class="ag-icon ag-icon-tree-indeterminate"></span>',
          disabled: column.colDef.lockVisible
        },
        ...defaultItems,
      ];
    },

    refreshGrid() {
      this.hideCellTooltip();
      gridApi.ensureIndexVisible(0);
      gridApi.purgeServerSideCache([]);
      this.expandedDetailRowNodes = {};
      gridApi.deselectAll();
      this.selectedCalls = [];
    },

    togglePinnedColumns() {
      if (this.isPinnedCollapsed) {
        this.showHiddenPinnedColumns();
        this.isPinnedCollapsed = false;
      } else {
        const columns = gridColumnApi.getDisplayedRightColumns().map(column => column.colId);
        gridColumnApi.setColumnsVisible(columns, false);
        this.isPinnedCollapsed = true;
      }
    },

    showHiddenPinnedColumns() {
      const columns = gridColumnApi?.getColumnState()?.filter(col => col.pinned =='right')?.map(col => col.colId);
      gridColumnApi?.setColumnsVisible(columns, true);
    },

    toggleCallLogsColumnsPanel() {
      if (gridApi.isToolPanelShowing()) {
        gridApi.closeToolPanel('columns');
      } else {
        gridApi.openToolPanel('columns');
        gridApi.gridCore.getRefElement('eFilterTextField').querySelector('input').focus();
      }
    },

    annotateMultipleCalls({ tag, callIds }) {
      this.$emit('annotateMultipleCalls', { tag, callIds });
    },

    handleBulkBlockNumbers(numbers) {
      this.$emit('blockMultipleNumbers', numbers);
    },

    handleBulkCallAdjustment({ callIds, data }) {
      this.$emit('adjustMultipleCalls', { callIds, data });
    },

    handleBulkCallAdjustmentByLogs({ callLogs, data }) {
      this.$emit('adjustMultipleCallsByLogs', { callLogs, data });
    },

    handleBulkCallTranscription({ callIds }) {
      this.$emit('transcribeMultipleCalls', { callIds });
    },

    handleBulkCallTranscriptionByLogs({ callLog, data }) {
      this.$emit('transcribeMultipleCallsByLogs', { callLog, data });
    },

    clearRowSelection() {
      gridApi.deselectAll();
      this.selectedCalls = [];
    },

    refreshActionColumn() {
      if (gridApi) {
        gridApi.refreshCells({
          columns: [ACTION_COLUMN_FIELD_NAME],
          force: true,
          suppressFlash: true
        });
      }
    },

    exportReport() {
      this.$emit('exportReport');
    },

    exportReportJSONRequestBody() {
      this.$emit('exportReportJSONRequestBody');
    }
  },
  i18n: {
    locale: 'en',
    messages,
    sharedMessages
  }
};
</script>

<style lang="scss" scoped>
.bulk-actions-dropdown {
  .dropdown-menu {
    li {
      .bulk-actions-dropdown__btn {
        border-bottom-width: 0;
      }
    }

    li:last-child {
      .bulk-actions-dropdown__btn {
        border-bottom-width: 1px;
      }
    }
  }
}

.bulk-actions-dropdown__btn {
  font-size: 10px;
  line-height: 19px;
  text-align: left !important;
}

#reporting-call-logs__column_btn + .bulk-actions-dropdown {
  margin-left: -1px;
 }

.clear-btn__icon--center {
  position: relative;
  top: 1px;
}

.btn-export-loader {
  .loader {
    right: 9px;
  }
}

@media (max-width: $screen-xs-max) {
  .btn-export-loader {
    .loader {
      right: 13px;
    }
  }
}
</style>

<style lang="scss">
.ag-grid--call-logs {
  height: 83vh;

  .ag-row-group-expanded, .ag-row-level-1 {
    background-color: var(--component-ag-grid-row--expanded);
    z-index: 2;
  }

  .filterable-cell {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .ag-side-buttons {
    display: none;
  }

  .ag-header-cell-menu-button {
    padding: 0 0 0 4px;
    margin-bottom: 2px;
  }

  .ag-right-aligned-header {
    .ag-header-cell-menu-button {
      padding: 0 4px 0 0;
    }
  }

  .ag-icon-menu {
    &::before {
      font: normal normal normal 14px/1 FontAwesome;
      content: "\f142";
    }
  }

  &.ag-grid--call-logs--show-right-arrow {
    .ag-header {
      overflow: visible;
    }

    .ag-pinned-right-header {
      @include pinned-header-scroll-icon('before', '\f050', 'left');
    }
  }

  &.ag-grid--call-logs--show-left-arrow {
    .ag-header {
      overflow: visible;
    }

    .ag-pinned-left-header {
      @include pinned-header-scroll-icon('after', '\f049', 'right');
      z-index: 1;
    }
  }

  &.ag-grid--custom div[row-id^="detail_"].ag-row-hover {
    background-color: var(--component-ag-grid-row--expanded) !important;
  }
}

.ag-grid-call-logs__container {
  position: relative;
}

.ag-grid-call-logs__collapse-right-pinned-btn {
  position: absolute;
  right: 1px;
  color: var(--reset-body-font-color);
  background: var(--component-table__header-row-bg);
  padding: 8px 5px;
  height: 32px;
  opacity: 0.6;
  cursor: pointer;
  z-index: 1;
  top: 1px;
  width: 16px;
}

// Keep right scroll arrow shown when right pinned header is hidden
.ag-grid--call-logs--hide-pinned-columns {
  .ag-pinned-right-header {
    display: inline-block !important; // This overrides the `display: none !important;`
  }
}

.mark-call {
  position: relative;
}

.mark-call__status {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 8px 8px 0 0;
}

.mark-call__status--live {
  border-color: $mark-green-color transparent transparent transparent;
}

.mark-call__status--converted {
  border-color: $mark-white-color transparent transparent transparent;
}

.mark-call__status--blocked {
  border-color: $mark-red-color transparent transparent transparent;
}

.mark-call__status--completed {
  border-color: $mark-yellow-color transparent transparent transparent;
}

.ag-grid__overlay-form-box {
  position: absolute;
  top: 33px;
  left: 0;
  right: 0;
  z-index: 3;
  background: var(--component-ag-grid__overlay-form-bg);
}

.ag-grid__overlay-form-box-title {
  border-bottom: 1px solid var(--component-form__input-bg-default);
  padding: 10px 15px;
  font-size: 20px;
}

.ag-grid__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba($color: #000000, $alpha: 0.7);
  z-index: 2;
}
</style>